/**
 * Auth Callback Page - Trade Loop v2.0
 *
 * Handles Supabase Auth callbacks (email confirmation, password reset, etc.)
 * Parses tokens from URL and redirects to appropriate page.
 */

import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import * as authService from "@/services/authService";
import tradeLoopLogo from "@/assets/trade-loop-logo.svg";
import { safeRedirectPath } from "@/utils/safeRedirect";
import { isNative } from "@/utils/platform";

export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { handleAuthCallback } = useAuth();
  // OAuth sign-ins land here too — the redirect_to sent to Supabase carries
  // ?provider=google|apple so we can show sign-in copy instead of the
  // email-confirmation wording. Only the copy branches on this; the token
  // handling below is provider-agnostic, because by the time Apple's
  // form_post callback has been absorbed by Supabase what arrives here is an
  // ordinary hash redirect indistinguishable from Google's.
  const providerParam = searchParams.get("provider");
  const providerLabel =
    providerParam === "google" ? "Google" : providerParam === "apple" ? "Apple" : null;
  const isOAuth = providerLabel !== null;
  const [status, setStatus] = useState<"processing" | "success" | "error">("processing");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>(
    isOAuth ? `Signed in with ${providerLabel}!` : "Email confirmed!"
  );
  const [redirectMessage, setRedirectMessage] = useState<string>(
    isOAuth ? "Taking you to Trade Loop..." : "Redirecting you to complete your profile..."
  );

  // Use ref to prevent processing multiple times (React strict mode / re-renders)
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Only process once
    if (hasProcessed.current) return;

    const processCallback = async () => {
      try {
        // Get the redirect destination from query params (default to /edit-profile)
        // Strip any query parameters from the redirect path to avoid doubling
        // Sanitized: `redirect` is attacker-controllable on a public URL, and a
        // protocol-relative value like `//evil.com` would navigate off-site
        // (utils/safeRedirect.ts). The existing split('?') only trimmed query params —
        // it did nothing about the destination itself.
        const rawRedirect = safeRedirectPath(searchParams.get("redirect"), "/edit-profile");
        let redirectTo = rawRedirect.split('?')[0]; // Remove query params if any

        // If a referral invite was pending when the user kicked off auth,
        // route them to the referrals page so the invite can be claimed. The
        // emailRedirectTo configured on the backend is hardcoded to
        // /edit-profile, so localStorage is the only signal we have here.
        const pendingReferralInviteToken = localStorage.getItem('pendingReferralInviteToken');
        if (pendingReferralInviteToken) {
          redirectTo = `/referrals?claimToken=${encodeURIComponent(pendingReferralInviteToken)}`;
        }

        // Supabase puts the tokens in the URL hash for email confirmation
        // The hash contains: #access_token=...&refresh_token=...&type=signup
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get("access_token");
        const refreshToken = hashParams.get("refresh_token");
        const type = hashParams.get("type");

        // Debug logging to see what we're getting
        const message = hashParams.get("message");
        console.log('[AuthCallback] Processing callback:', {
          rawRedirect,
          redirectTo,
          searchParams: Object.fromEntries(searchParams.entries())
        });
        console.log('[AuthCallback] URL hash params:', {
          accessToken: accessToken ? 'present' : 'missing',
          refreshToken: refreshToken ? 'present' : 'missing',
          type: type || 'none',
          message: message || 'none',
          fullHash: window.location.hash,
          allParams: Object.fromEntries(hashParams.entries())
        });

        // Check for email change confirmation message
        if (message && message.includes("Confirmation link accepted")) {
          hasProcessed.current = true;
          setSuccessMessage("Email verification in progress!");
          setRedirectMessage("Please check your other email to complete the change...");
          setStatus("success");

          // Clear URL hash for security
          window.history.replaceState(null, '', window.location.pathname + window.location.search);

          // Longer delay since user needs to take another action
          setTimeout(() => {
            navigate("/account", { replace: true });
          }, 4000);
        } else if (accessToken && refreshToken) {
          // Mark as processed BEFORE clearing tokens
          hasProcessed.current = true;

          // Immediately clear tokens from URL for security
          window.history.replaceState(null, '', window.location.pathname + window.location.search);

          // Beta-gated OAuth (BETA_GATED_OAUTH_PLAN.md §5): thread the sign-in
          // intent (login vs signup) and, for signup, the pre-validated beta key
          // + optional phone + CRM answer stashed before the redirect. Read from
          // the query / localStorage here and forward them to the backend gate. An absent
          // intent (e.g. the email-confirmation flow) is left undefined so the
          // backend normalises it to 'login'. The stash is single-use — clear it
          // immediately regardless of the gate's outcome.
          const intentParam = searchParams.get("intent");
          const intent =
            intentParam === "signup" ? "signup" : intentParam === "login" ? "login" : undefined;
          const { phone, firstName, lastName } =
            authService.readPendingOAuthSignup();
          authService.clearPendingOAuthSignup();

          // A `/connect/<uuid>` invite click writes the referrer id to
          // localStorage before it forwards to /login (see LoginPage.tsx). On the
          // OAuth round-trip that value survives here and is the only signal the
          // callback has that this signup came from an invite. Forwarded to the
          // backend so establishOAuthSession's referral hookup fires; cleared on
          // BOTH branches (success and error) so a stale value cannot latch onto
          // an unrelated later signup. Backend ignores it on `intent === 'login'`.
          const referralCode = localStorage.getItem('pendingReferrerId') || undefined;

          try {
            await handleAuthCallback(accessToken, refreshToken, {
              intent,
              phone,
              firstName,
              lastName,
              referralCode,
            });
          } finally {
            if (referralCode) {
              localStorage.removeItem('pendingReferrerId');
            }
          }

          setStatus("success");

          // Short delay to show success message, then redirect
          setTimeout(() => {
            navigate(redirectTo, { replace: true });
          }, 1500);
        } else if (type === "recovery") {
          hasProcessed.current = true;
          // Password reset flow - redirect to password reset page
          navigate("/auth/reset-password", { replace: true });
        } else if (type === "email_change" || type === "email") {
          hasProcessed.current = true;

          // Email change confirmation with tokens present
          // The user clicked the verification link, we have new tokens
          if (accessToken && refreshToken) {
            // Clear URL hash for security BEFORE processing
            window.history.replaceState(null, '', window.location.pathname + window.location.search);

            // Update session with new tokens (this will refresh the user's session)
            await handleAuthCallback(accessToken, refreshToken);

            setSuccessMessage("Email changed successfully!");

            // Different message depending on where the email link was opened. This used to
            // ask `display-mode: standalone` (i.e. "is this the installed PWA"); with the PWA
            // retired the only app is the native one, so `isNative` is both the correct
            // question and a more reliable one than the media query ever was.
            if (!isNative) {
              setRedirectMessage("Please return to your app and refresh to see the changes.");
            } else {
              setRedirectMessage("Returning to your account...");
            }

            setStatus("success");

            // Redirect to account page
            setTimeout(() => {
              navigate("/account", { replace: true });
            }, isNative ? 1500 : 3000); // Longer delay in the browser, to read the message
          } else {
            // No tokens but email_change type - edge case
            setSuccessMessage("Email verification received!");
            setRedirectMessage("Please refresh your app to see the changes...");
            setStatus("success");

            // Clear URL hash for security
            window.history.replaceState(null, '', window.location.pathname + window.location.search);

            setTimeout(() => {
              navigate("/account", { replace: true });
            }, 2000);
          }
        } else if (!hasProcessed.current) {
          // Only show error if we haven't processed yet
          // (This prevents showing error on re-render after hash was cleared)
          hasProcessed.current = true;
          setStatus("error");
          setErrorMessage("No authentication tokens found");
          setTimeout(() => {
            navigate("/login", { replace: true });
          }, 3000);
        }
      } catch (error: any) {
        // Beta-gate rejections (BETA_GATED_OAUTH_PLAN.md §2) are expected
        // outcomes, not failures: route the user back to the signup tab with a
        // graceful message instead of a raw error toast/screen. The backend
        // reports the reason via a `code` on the error payload.
        // APPLE_HIDDEN_EMAIL is a gate rejection too, not an error: the user has
        // an account, Apple's relay alias just cannot be matched to it
        // (APPLE_HIDDEN_EMAIL_LINKING.md §6). It must be in this allowlist or
        // the web bounce never delivers the code and the user gets the generic
        // error screen instead of the instruction that actually helps.
        const gateCode = error?.data?.code;
        if (
          gateCode === "NO_ACCOUNT" ||
          gateCode === "APPLE_HIDDEN_EMAIL"
        ) {
          hasProcessed.current = true;
          // Carry the provider so the signup-tab notice can name it. Without
          // this the copy has to guess, and it guessed "Google" for everyone.
          navigate("/login", {
            replace: true,
            state: { oauthError: gateCode, oauthProvider: providerParam ?? undefined },
          });
          return;
        }

        console.error("Auth callback error:", error);
        setStatus("error");
        setErrorMessage(error.message || "An error occurred during authentication");

        // Redirect to login after a delay
        setTimeout(() => {
          navigate("/login", { replace: true });
        }, 3000);
      }
    };

    processCallback();
  }, [navigate, searchParams, handleAuthCallback]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50/30 to-white flex items-center justify-center">
      <div className="text-center px-4">
        <img src={tradeLoopLogo} alt="Trade Loop" className="h-16 w-auto mx-auto mb-8" />

        {status === "processing" && (
          <div className="animate-in fade-in duration-300">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {isOAuth ? "Signing you in..." : "Confirming your email..."}
            </h2>
            <p className="text-gray-600">Please wait while we verify your account.</p>
          </div>
        )}

        {status === "success" && (
          <div className="animate-in fade-in duration-300">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {successMessage}
            </h2>
            <p className="text-gray-600">{redirectMessage}</p>
          </div>
        )}

        {status === "error" && (
          <div className="animate-in fade-in duration-300">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Something went wrong
            </h2>
            <p className="text-gray-600 mb-4">{errorMessage}</p>
            <p className="text-sm text-gray-500">Redirecting to login...</p>
          </div>
        )}
      </div>
    </div>
  );
}
